/**
 * TimelineControls component (Task 7.2)
 *
 * Playback controls for the timeline editor.
 *
 * Features:
 * - Play/Pause button (toggles isPlaying in timelineStore)
 * - Stop button (halts playback, resets playhead to 0)
 * - Speed multiplier dropdown: 0.25x, 0.5x, 1x, 2x, 4x
 * - Loop toggle button
 * - Timestamp display: MM:SS.sss format
 */

import { Play, Pause, Square, Repeat } from 'lucide-react';
import { useTimelineStore } from '../../stores/timelineStore';
import * as Select from '@radix-ui/react-select';

/**
 * Available playback speed multipliers
 */
const SPEED_MULTIPLIERS = [0.25, 0.5, 1, 2, 4];

/**
 * Format time as MM:SS.sss timestamp
 */
function formatTimestamp(seconds: number): string {
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const ms = Math.floor((seconds - totalSeconds) * 1000);

  const mm = minutes.toString().padStart(2, '0');
  const ss = secs.toString().padStart(2, '0');
  const sss = ms.toString().padStart(3, '0');

  return `${mm}:${ss}.${sss}`;
}

export interface TimelineControlsProps {
  /**
   * Whether to enable loop playback
   * @default false
   */
  loopEnabled?: boolean;

  /**
   * Callback when loop is toggled
   */
  onLoopToggle?: (enabled: boolean) => void;
}

export function TimelineControls({
  loopEnabled = false,
  onLoopToggle,
}: TimelineControlsProps) {
  const isPlaying = useTimelineStore((state) => state.isPlaying);
  const playheadPosition = useTimelineStore((state) => state.playheadPosition);
  const playbackSpeed = useTimelineStore((state) => state.playbackSpeed);
  const togglePlayback = useTimelineStore((state) => state.togglePlayback);
  const stopPlayback = useTimelineStore((state) => state.stopPlayback);
  const setPlaybackSpeed = useTimelineStore((state) => state.setPlaybackSpeed);
  const setPlayheadPosition = useTimelineStore(
    (state) => state.setPlayheadPosition
  );

  const handleStop = () => {
    stopPlayback();
    setPlayheadPosition(0);
  };

  const handleSpeedChange = (value: string) => {
    const speed = parseFloat(value);
    setPlaybackSpeed(speed);
  };

  const handleLoopToggle = () => {
    onLoopToggle?.(!loopEnabled);
  };

  return (
    <div
      className="flex items-center gap-4 px-4 py-3 bg-[#262626] border-b border-[#404040]"
      data-testid="timeline-controls"
    >
      {/* Playback controls */}
      <div className="flex items-center gap-2">
        {/* Play/Pause button */}
        <button
          onClick={togglePlayback}
          className="flex items-center justify-center w-10 h-10 rounded bg-[#1e1e1e] hover:bg-[#2a2a2a] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          data-testid="play-pause-button"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5 text-white" />
          ) : (
            <Play className="w-5 h-5 text-white ml-0.5" />
          )}
        </button>

        {/* Stop button */}
        <button
          onClick={handleStop}
          className="flex items-center justify-center w-10 h-10 rounded bg-[#1e1e1e] hover:bg-[#2a2a2a] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Stop"
          data-testid="stop-button"
        >
          <Square className="w-5 h-5 text-white" />
        </button>

        {/* Loop toggle */}
        <button
          onClick={handleLoopToggle}
          className={`flex items-center justify-center w-10 h-10 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            loopEnabled
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-[#1e1e1e] hover:bg-[#2a2a2a]'
          }`}
          aria-label="Loop"
          aria-pressed={loopEnabled}
          data-testid="loop-button"
        >
          <Repeat className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Speed multiplier dropdown */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-[#a3a3a3]">Speed:</span>
        <Select.Root
          value={playbackSpeed.toString()}
          onValueChange={handleSpeedChange}
        >
          <Select.Trigger
            className="flex items-center gap-2 px-3 py-1.5 text-sm rounded bg-[#1e1e1e] text-white hover:bg-[#2a2a2a] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[80px]"
            aria-label="Playback speed"
            data-testid="speed-selector"
          >
            <Select.Value />
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              className="overflow-hidden bg-[#262626] rounded shadow-lg border border-[#404040]"
              position="popper"
              sideOffset={5}
            >
              <Select.Viewport className="p-1">
                {SPEED_MULTIPLIERS.map((speed) => (
                  <Select.Item
                    key={speed}
                    value={speed.toString()}
                    className="relative flex items-center px-8 py-2 text-sm text-white rounded outline-none cursor-pointer select-none hover:bg-[#1e1e1e] focus:bg-[#1e1e1e] data-[state=checked]:bg-blue-600"
                  >
                    <Select.ItemText>{speed}x</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {/* Timestamp display */}
      <div className="flex items-center gap-2 ml-4">
        <span className="text-sm text-[#a3a3a3]">Time:</span>
        <span
          className="font-mono text-sm text-white"
          data-testid="timestamp-display"
        >
          {formatTimestamp(playheadPosition)}
        </span>
      </div>
    </div>
  );
}
